import { EmailServiceEndpoints, EmailServiceParams } from 'lib/constants/email-service-params';
import { useCaptchaGeneration } from 'lib/hooks/useCaptchaGeneration';
import React, { useState, useCallback } from 'react';

interface EmailBoxProps {
  emailName?: string;
  customAttributes?: Record<string, string> | Array<{ Name: string; Value: string }>;
}

const EmailBox: React.FC<EmailBoxProps> = ({
  emailName,
  // TODO: Only PAGE_URL is required. Rest are optional and need to be changed based on requirement.
  customAttributes = [
    {
      Name: EmailServiceParams.PAGE_URL,
      Value: typeof window !== 'undefined' ? window.location.href : '',
    },
    { Name: EmailServiceParams.TITLE, Value: 'Test' },
    {
      Name: EmailServiceParams.DESCRIPTION,
      Value:
        'A spoonful of marshmallow crème tops steaming homemade cocoa with rich chocolate flavor and a hint of spice.',
    },
    {
      Name: EmailServiceParams.IMAGE,
      Value:
        'https://mojo.generalmills.com/api/public/content/Bs8-_txpCUGqrl-dBSf4HQ_webp_base.webp?v=f23247c0&t=e724eca7b3c24a8aaa6e089ed9e611fd',
    },
    { Name: EmailServiceParams.CONTENT_TYPE, Value: 'Recipe' }, //TODO: update this according to requirement
  ],
}) => {
  const [emailAddresses, setEmailAddresses] = useState('');
  const [fromName, setFromName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Use our optimized captcha hook
  const { captchaToken } = useCaptchaGeneration('email_submission');

  // Parse and validate email addresses
  const parseEmails = useCallback((input: string): string[] => {
    return input
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  }, []);

  // Reset the form
  const resetForm = useCallback(() => {
    setEmailAddresses('');
    setFromName('');
    setSubmitError('');
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    try {
      // Get a fresh captcha token
      const token = await captchaToken();
      if (!token) {
        setSubmitError('Security verification failed. Please refresh the page and try again.');
        return;
      }

      // Parse and validate email addresses
      const addresses = parseEmails(emailAddresses);

      if (addresses.length === 0) {
        setSubmitError('Please enter at least one valid email address');
        return;
      }

      if (!fromName.trim()) {
        setSubmitError('Please enter your name');
        return;
      }

      // Start submission
      setIsSubmitting(true);

      // Add FROM_FIRST_NAME to customAttributes
      let updatedAttributes;
      if (Array.isArray(customAttributes)) {
        updatedAttributes = [
          ...customAttributes,
          { Name: EmailServiceParams.FROM_FIRST_NAME, Value: fromName },
        ];
      }

      // Call the API to send the email
      const response = await fetch(EmailServiceEndpoints.SEND_EMAIL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailAddresses: addresses,
          emailName,
          customAttributes: updatedAttributes,
          captchaToken: token,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to send email');
      }

      // Handle success
      resetForm();
    } catch (error) {
      // Handle error
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="email-box">
      <h2>Email Box</h2>

      {submitError && (
        <div className="error-message" role="alert">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="emailAddresses">Email Addresses:</label>
          <input
            type="email"
            id="emailAddresses"
            name="emailAddresses"
            value={emailAddresses}
            onChange={(e) => setEmailAddresses(e.target.value)}
            placeholder="Enter email addresses separated by commas"
            required
            disabled={isSubmitting}
            aria-describedby="email-hint"
          />
        </div>

        <div className="form-group">
          <label htmlFor="fromName">From Name:</label>
          <input
            type="text"
            id="fromName"
            name="fromName"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Enter your name"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Show loading indicator when reCAPTCHA is not ready */}

        <button
          type="submit"
          className="send-button"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default EmailBox;
